import { useEffect, useRef, useState } from "react";
import { Keyboard, Pressable } from "react-native";
import { IconButton, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import TooltipWrapper from "../TooltipWrapper";
import type { ToolActionProps } from "./ToolAction";
import ToolAction from "./ToolAction";

export type TextModeChatComposerProps = {
	onIconPress?: () => void;
	ToolActions?: ToolActionProps;
	textInputProps: { placeholder?: string; icon?: string };
	showSuggestions?: boolean;
	suggestions?: { text: string; onPress: () => void }[];
	disableSearch?: boolean;
	isItemAnswered?: boolean;
	onAbortResponse: () => void;
	interceptResponseQuery: (query: string) => void;
	inputQuery: string;
	onChangeToVoiceMode?: () => void;
	STTMode?: boolean;
	isVoiceModeEnabled?: boolean;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Complex component with many conditional renderings
const TextModeChatComposer = (props: TextModeChatComposerProps) => {
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const theme = useAppTheme();
	const [isFocused, setIsFocused] = useState(false);
	const [isMultiline, setIsMultiline] = useState(false);

	const { inputQuery } = props;

	const [localSearchQuery, setLocalSearchQuery] = useState("");

	useEffect(() => {
		if (props.isItemAnswered === false) {
			return;
		}
		setLocalSearchQuery(inputQuery || "");
	}, [inputQuery, props.isItemAnswered]);

	useEffect(() => {
		if (props.isItemAnswered === false) {
			setLocalSearchQuery("");
			textareaRef.current?.blur();
			return;
		}
		textareaRef.current?.focus();
	}, [props.isItemAnswered]);

	const adjustTextareaHeight = () => {
		if (textareaRef.current) {
			textareaRef.current.style.height = "24px";
			const scrollHeight = textareaRef.current.scrollHeight;

			const needsMultiline = scrollHeight > 24;
			setIsMultiline(needsMultiline);

			const newHeight = needsMultiline ? Math.min(scrollHeight, 60) : 24;
			textareaRef.current.style.height = `${newHeight}px`;
		}
	};
	const handleOnAction = () => {
		if (props.isItemAnswered === false) {
			return;
		}
		if (props.disableSearch) {
			props.onAbortResponse();
		} else {
			handleOnSubmit(localSearchQuery);
			setLocalSearchQuery("");
		}
	};

	const handleOnSubmit = (query: string) => {
		if (props.isItemAnswered === false) {
			return;
		}
		if (query.trim().length > 0) {
			Keyboard.dismiss();
			props.interceptResponseQuery(query.trim());
		}
	};

	const isActionDisabled =
		props.isItemAnswered === false
			? true
			: props.disableSearch
				? false
				: localSearchQuery.length === 0;

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleOnAction();
		}
		if (e.key === "Escape") {
			if (localSearchQuery.length > 0) {
				setLocalSearchQuery("");
			} else {
				textareaRef?.current?.blur();
			}
		}
	};
	const shouldShowBorder =
		isFocused || localSearchQuery.length > 0 || props.showSuggestions;

	return (
		<Box>
			<Box
				backgroundColor={
					props.ToolActions ? theme.colors.surfaceContainerHigh : "transparent"
				}
				borderRadius={shouldShowBorder ? 24 : 100}
				borderTopLeftRadius={shouldShowBorder ? 24 : 100}
				borderTopRightRadius={shouldShowBorder ? 24 : 100}
			>
				{props.showSuggestions && (
					<Box paddingTop={10} paddingHorizontal={12} paddingBottom={8}>
						<Box gap={6}>
							{props.suggestions?.map((suggestion) => (
								<Pressable
									key={suggestion.text}
									onPress={suggestion.onPress}
									style={{ cursor: "pointer" }}
								>
									<Box>
										<Text
											variant="bodyMedium"
											style={{
												color: theme.colors.onSurfaceVariant,
												flexWrap: "wrap",
											}}
										>
											{suggestion.text}
										</Text>
									</Box>
								</Pressable>
							))}
						</Box>
					</Box>
				)}
				<Box>
					{props.ToolActions && (
						<Box paddingHorizontal={12} paddingTop={6}>
							<ToolAction
								key={props.ToolActions.text}
								icon={props.ToolActions.icon}
								text={props.ToolActions.text}
							/>
						</Box>
					)}
					<Box
						borderRadius={24}
						minHeight={44}
						maxHeight={80}
						borderWidth={2}
						borderColor={
							shouldShowBorder
								? theme.colors.primary
								: theme.colors.onSurfaceVariant
						}
						flexDirection="row"
						alignItems="stretch"
						style={{ position: "relative" }}
					>
						<Box
							flex={1}
							minWidth={0}
							paddingLeft={20}
							paddingRight={44}
							justifyContent={isMultiline ? "flex-start" : "center"}
							paddingVertical={isMultiline ? 8 : 0}
						>
							<textarea
								ref={textareaRef}
								placeholder={props.textInputProps.placeholder}
								aria-label={
									props.textInputProps.placeholder || "Chat message input"
								}
								disabled={props.disableSearch || props.isItemAnswered === false}
								value={localSearchQuery}
								onChange={(e) => {
									setLocalSearchQuery(e.target.value);
									requestAnimationFrame(adjustTextareaHeight);
								}}
								onFocus={() => setIsFocused(true)}
								onBlur={() => setIsFocused(false)}
								onKeyDown={handleKeyDown}
								style={{
									width: "100%",
									height: isMultiline ? "auto" : "24px",
									minHeight: "24px",
									maxHeight: "60px",
									margin: 0,
									padding: 0,
									border: "none",
									outline: "none",
									backgroundColor: "transparent",
									resize: "none",
									overflow: "auto",
									overflowX: "auto",
									color: theme.colors.text,
									fontSize: 16,
									lineHeight: "24px",
									fontFamily: "inherit",
								}}
							/>
						</Box>
						<Box
							style={{
								position: "absolute",
								right: 6,
								bottom: 6,
							}}
						>
							<TooltipWrapper
								title={
									props.disableSearch
										? "Stop"
										: localSearchQuery.length > 0
											? "Send message"
											: props.isVoiceModeEnabled
												? "Voice mode"
												: "Type and enter to send a message"
								}
								style={{
									padding: 0,
									cursor:
										props.isItemAnswered === false
											? "not-allowed"
											: props.disableSearch || localSearchQuery.length > 0
												? "pointer"
												: "not-allowed",
								}}
							>
								<IconButton
									mode="contained"
									disabled={isActionDisabled}
									containerColor={theme.colors.primary}
									size={16}
									style={{
										width: 28,
										height: 28,
										margin: 0,
									}}
									iconColor={theme.colors.onPrimary}
									icon={props.disableSearch ? "stop" : "arrow-up"}
									onPress={handleOnAction}
								/>
							</TooltipWrapper>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default TextModeChatComposer;
